import {
    ImageGalleryEnrichedImage,
    ImageShape,
    ImageTemplate,
    ImageTemplateMatchResult,
} from '../../ImageGallery.types';
import { IMAGE_MATCHING_CONFIG } from '../ImageGalleryGrid.constants';

/**
 * Returns the score adjustment for a slot match based on whether a wildcard was used
 * and if the slot requires a large image.
 */
function getScoreForSlot(usedWildcard: boolean, requiresLarge: boolean): number {
    if (usedWildcard) {
        return requiresLarge ? IMAGE_MATCHING_CONFIG.scoreWildcardLarge : IMAGE_MATCHING_CONFIG.scoreWildcardNormal;
    }
    return requiresLarge ? IMAGE_MATCHING_CONFIG.scoreExactLarge : IMAGE_MATCHING_CONFIG.scoreExactNormal;
}

/**
 * Finds the best matching images by shape query sorted by originalIndex ascending,
 * considering mustFill and large image slot conditions.
 */
function findBestMatch(
    remaining: ImageGalleryEnrichedImage[],
    shapeQuery: ImageShape,
    mustFill: boolean,
    template: ImageTemplate
): { img: ImageGalleryEnrichedImage; i: number }[] {
    return remaining
        .map((img, i) => ({ img, i }))
        .filter(
            ({ img }) =>
                img.shape === shapeQuery &&
                (!mustFill || img.isLarge || !template.slots.some((slot) => slot.requiresLarge))
        )
        .sort((a, b) => a.img.originalIndex - b.img.originalIndex);
}

/**
 * Attempts to find a matching image index for a given slot from remaining images,
 * applying fallback logic allowing shape wildcards and verifying large image requirements.
 */
function findMatchIndexForSlot(
    remaining: ImageGalleryEnrichedImage[],
    slot: { shape: ImageShape; requiresLarge?: boolean },
    mustFill: boolean,
    template: ImageTemplate
): { matchIndex: number; usedWildcard: boolean } {
    const requiresLargeImage = mustFill ? false : slot.requiresLarge || false;

    // 1) Exact match candidates
    let candidates = findBestMatch(remaining, slot.shape, mustFill, template).filter(
        ({ img }) => !requiresLargeImage || img.isLarge
    );
    if (candidates.length > 0) {
        return { matchIndex: candidates[0].i, usedWildcard: false };
    }

    // 2) Fallback: non-square slots allow square images
    if (slot.shape !== 'square') {
        candidates = findBestMatch(remaining, 'square', mustFill, template).filter(
            ({ img }) => !requiresLargeImage || img.isLarge
        );
        if (candidates.length > 0) {
            return { matchIndex: candidates[0].i, usedWildcard: true };
        }
    }

    // 3) Fallback for square slots: allow non-square images
    if (slot.shape === 'square') {
        const nonSquareCandidates = remaining
            .map((img, i) => ({ img, i }))
            .filter(({ img }) => img.shape !== 'square' && (!requiresLargeImage || img.isLarge))
            .sort((a, b) => a.img.originalIndex - b.img.originalIndex);
        if (nonSquareCandidates.length > 0) {
            return { matchIndex: nonSquareCandidates[0].i, usedWildcard: true };
        }
    }

    return { matchIndex: -1, usedWildcard: false };
}

/**
 * Calculates the final score for a template match based on slots filled, base score,
 * template priority, recent usage penalty, and wildcard penalties.
 */
function calculateFinalScore(
    usedImagesBySlot: (ImageGalleryEnrichedImage | null)[],
    score: number,
    wildcardsUsed: number,
    template: ImageTemplate,
    recentTemplates: string[]
): number {
    const filledSlotsCount = usedImagesBySlot.filter(Boolean).length;
    const totalSlots = template.slots.length;

    const templatePriority = template.priority ?? 0;
    const recencyIndex = recentTemplates.indexOf(template.id);
    const recencyPenalty =
        recencyIndex === -1 ? 0 : -IMAGE_MATCHING_CONFIG.recencyBasePenalty * (recentTemplates.length - recencyIndex);

    const partialFillScore = (filledSlotsCount / totalSlots) * 10;
    const wildcardPenalty = wildcardsUsed * IMAGE_MATCHING_CONFIG.wildcardPenalty;

    return partialFillScore + score + templatePriority + recencyPenalty - wildcardPenalty;
}

/**
 * Attempts to find the best matching image layout template for a given set of images.
 *
 * This function assigns images to template slots prioritizing matches by:
 * - Image shape (exact or fallback wildcard matches)
 * - Image size requirements (large images when required)
 * - Image original index for deterministic ordering favoring earlier images
 *
 * The matching process supports:
 * - Fallback strategies for shape mismatches (e.g., square images filling non-square slots)
 * - Forced assignment for mustFill templates, ensuring all slots are filled even if images don't strictly meet criteria
 * - Scoring based on match exactness, image size, template priority, and recency to encourage diverse layouts
 *
 * The scoring system includes penalties for wildcards and recent template reuse to optimize variation.
 *
 * @param images - Array of enriched images to assign, each containing shape, size, and indexing metadata.
 * @param templates - Candidate templates describing slots with shape and size requirements.
 * @param recentTemplates - IDs of templates recently used, ordered from most to least recent, to apply recency penalties.
 * @param allowPartial - When true, allows partial slot filling when full slot matching is unavailable.
 * @returns The best matching template with assigned images and a computed score, or null if no suitable match exists.
 *
 * @remarks
 * The algorithm performs a combinatorial scoring of each template and its potential image matches,
 * applying a fail-safe forced fill for mustFill templates to guarantee slot population.
 * Matches are sorted by their composite score, balancing preference for strict matches and template diversity.
 */
export function matchTemplates(
    images: ImageGalleryEnrichedImage[],
    templates: ImageTemplate[],
    recentTemplates: string[] = [],
    allowPartial = false
): ImageTemplateMatchResult | null {
    const matches = templates
        .map((template) => {
            const mustFill = template.mustFill ?? false;
            const requireFullFill = mustFill || !allowPartial;

            const remaining = [...images];
            const usedImagesBySlot: (ImageGalleryEnrichedImage | null)[] = new Array(template.slots.length).fill(null);

            const initialAcc = { valid: true, score: 0, wildcardsUsed: 0 };

            const reduceResult = template.slots.reduce((acc, slot, slotIndex) => {
                if (!acc.valid) return acc;

                const { matchIndex, usedWildcard } = findMatchIndexForSlot(remaining, slot, mustFill, template);

                if (matchIndex === -1) {
                    if (mustFill) {
                        if (remaining.length > 0) {
                            const forcedImage = remaining.shift();
                            if (!forcedImage) {
                                acc.valid = false;
                                return acc;
                            }
                            usedImagesBySlot[slotIndex] = forcedImage;
                            acc.score += 0;
                            return acc;
                        }
                    }
                    if (!requireFullFill) {
                        usedImagesBySlot[slotIndex] = null;
                        return acc;
                    }
                    acc.valid = false;
                    return acc;
                }

                const matchedImage = remaining.splice(matchIndex, 1)[0];
                usedImagesBySlot[slotIndex] = matchedImage;

                acc.score += getScoreForSlot(usedWildcard, slot.requiresLarge || false);
                if (usedWildcard) acc.wildcardsUsed += 1;

                return acc;
            }, initialAcc);

            const { valid, score, wildcardsUsed } = reduceResult;

            if (!valid) return null;

            const filledSlotsCount = usedImagesBySlot.filter(Boolean).length;

            if (filledSlotsCount === 0) return null;
            if ((mustFill || !allowPartial) && filledSlotsCount < template.slots.length) return null;

            const finalScore = calculateFinalScore(usedImagesBySlot, score, wildcardsUsed, template, recentTemplates);

            return {
                template,
                usedImagesBySlot,
                score: finalScore,
            };
        })
        .filter((x): x is ImageTemplateMatchResult => x !== null);

    if (matches.length === 0) return null;

    matches.sort((a, b) => b.score - a.score);

    return matches[0];
}
